import { stripHtml } from './text-formatting';
import { applyTemplateVariables, extractParametersFromSendStructure } from './file-upload-gsc';

type Props = {
  currentUserId: string | null;
  conversation: any;
  t?: any;
};

export const profileType = [
  {
    name: 'Variance Whatsapp Business',
    icon: 'hugeicons:whatsapp-business',
    type: 'WHATSAPP',
  },
  {
    name: 'Variance Mail',
    icon: 'material-symbols:mail-outline-rounded',
    type: 'EMAIL',
  },
  {
    name: 'Variance Voice',
    icon: 'ci:user-voice',
    type: 'VOICE',
  },
  {
    name: 'Variance Live Chat',
    icon: 'material-symbols:video-chat-outline',
    type: 'LIVE_CHAT',
  },
  {
    name: 'Variance Instagram',
    icon: 'mdi:instagram',
    type: 'INSTAGRAM',
  },
  {
    name: 'Variance Messenger Chat',
    icon: 'mingcute:messenger-fill',
    type: 'MESSENGER',
  },
  {
    name: 'SMS',
    icon: 'fa-solid:sms',
    type: 'SMS',
  },
  {
    name: 'LINE',
    icon: 'mage:line',
    type: 'LINE',
  },
  {
    name: 'TELEGRAM',
    icon: 'ic:outline-telegram',
    type: 'TELEGRAM',
  },
  {
    name: 'MICROSOFTTEAMS',
    icon: 'lineicons:microsoft-teams',
    type: 'MICROSOFTTEAMS',
  },
];

export const userType = [
  {
    name: 'Variance Whatsapp Business',
    icon: '/assets/icons/navbar/ic-variance-whatsapp-business.svg',
    type: 'WHATSAPP',
  },
  {
    name: 'Variance Mail',
    icon: '/assets/icons/navbar/ic-variance-mail.svg',
    type: 'EMAIL',
  },
  {
    name: 'Variance Voice',
    icon: '/assets/icons/navbar/ic-variance-voice.svg',
    type: 'VOICE',
  },
  {
    name: 'Variance Live Chat',
    icon: '/assets/icons/navbar/ic-variance-livechat.svg',
    type: 'LIVE_CHAT',
  },
  {
    name: 'Variance messenger Chat',
    icon: '/assets/icons/navbar/ic-variance-facebook.svg',
    type: 'MESSENGER',
  },
  {
    name: 'Variance Instagram Chat',
    icon: '/assets/icons/navbar/ic-variance-instagram.svg',
    type: 'INSTAGRAM',
  },
  // {
  //   name: 'SMS',
  //   icon: '/assets/icons/navbar/ic-variance-sms.svg', // Make sure this icon path exists
  //   type: 'SMS',
  // },
  // {
  //   name: 'LINE',
  //   type: 'LINE',
  //   icon: '/assets/icons/navbar/ic-variance-line.svg', // Make sure this icon path exists
  // },
];

export const labelData = () => [
  {
    labelName: 'Support',
    labelColor: 'warning',
    labelDescription: 'This is a support ticket',
  },
  {
    labelName: 'Sales',
    labelColor: 'primary',
    labelDescription: 'This is a sales-related ticket',
  },
  {
    labelName: 'Whatsapp',
    labelColor: 'secondary',
    labelDescription: 'This is a WhatsApp-related ticket',
  },
  {
    labelName: 'Billing',
    labelColor: 'error',
    labelDescription: 'This is a billing-related ticket',
  },
  {
    labelName: 'Technical',
    labelColor: 'info',
    labelDescription: 'This is a technical issue ticket',
  },
  {
    labelName: 'Feedback',
    labelColor: 'success',
    labelDescription: 'This is a customer feedback ticket',
  },
  {
    labelName: 'Urgent',
    labelColor: 'error',
    labelDescription: 'This is an urgent issue ticket',
  },
  {
    labelName: 'Feature Request',
    labelColor: 'success',
    labelDescription: 'This is a feature request ticket',
  },
  {
    labelName: 'Bug',
    labelColor: 'error',
    labelDescription: 'This is a bug report ticket',
  },
  {
    labelName: 'General Inquiry',
    labelColor: 'info',
    labelDescription: 'This is a general inquiry ticket',
  },
];

// Helper function to format phone numbers with + prefix
const formatPhoneNumber = (phone: string | null | undefined): string => {
  if (!phone) return '';
  // If phone already starts with +, return as is
  if (phone.startsWith('+')) return phone;
  // Otherwise, add + prefix
  return `+${phone}`;
};

export const getDisplayName = (channel: any, contact: any, conversation: any, t: any) => {
  if (channel?.channelType === 'MICROSOFTTEAMS' && conversation?.msExtraData?.channelName) {
    return `${conversation.msExtraData.channelName} (${t('CHANNEL')})`;
  }

  // Priority 1: First name + Last name
  if (contact.firstName || contact.lastName) {
    return `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
  }

  // Priority 2: Channel-specific contact info (phone/email based on channel type)
  const channelMap: Record<string, any> = {
    WHATSAPP: contact.phone ? formatPhoneNumber(contact.phone) : null,
    EMAIL: contact.email,
    VOICE: contact.phone ? formatPhoneNumber(contact.phone) : null,
    LIVE_CHAT: contact.phone ? formatPhoneNumber(contact.phone) : null,
    SMS: contact.phone ? formatPhoneNumber(contact.phone) : null,
  };

  if (channelMap[channel?.channelType]) {
    return channelMap[channel?.channelType];
  }

  // Priority 3: Any available phone or email
  if (contact.phone) {
    return formatPhoneNumber(contact.phone);
  }

  if (contact.email) {
    return contact.email;
  }

  // Priority 4: Unknown (last resort)
  return 'Unknown';
};

const getInteractiveMessageText = (message: any): string => {
  const metadata = message?.metadata;
  if (!metadata) return '';
  try {
    const parsedMeta = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
    return (
      parsedMeta?.interactive?.button_reply?.title ||
      parsedMeta?.interactive?.list_reply?.title ||
      parsedMeta?.button_reply?.title ||
      parsedMeta?.list_reply?.title ||
      parsedMeta?.interactive?.title ||
      parsedMeta?.title ||
      ''
    );
  } catch {
    return '';
  }
};

const processMessage = (
  message: any,
  contact: any,
  contactId: string,
  t: any,
  externalIdPrefix?: string
) => {
  if (!message) return { displayText: '', status: false };

  // ✅ Use messageType to determine if it's from the user (more reliable than senderId)
  const sender = message.messageType === 'OUTBOUND' ? `${t('YOU')}: ` : '';

  let messageText = '';

  // ✅ Universal content field - works for LINE, WhatsApp, and all other channels
  const messageContent = message.text || message.content || '';

  if (message.contentType === 'template') {
    try {
      let parsedTemplate;
      if (message?.metadata?.template?.extraData) {
        parsedTemplate = message.metadata.template.extraData;
      } else if (message.templateStructure) {
        parsedTemplate = JSON.parse(message.templateStructure);
      }

      // ✅ LINE template
      // ✅ LINE template
      if (externalIdPrefix === 'line' || message.channelType === 'LINE') {
        messageText =
          parsedTemplate?.components?.template?.columns?.[0]?.text ||
          parsedTemplate?.components?.template?.columns?.[0]?.title ||
          parsedTemplate?.components?.altText ||
          t('SENT_A_TEMPLATE');
      }
      // ✅ Other channels (WhatsApp, etc.)
      else {
        let parsedSend;
        if (message?.metadata?.template?.components) {
          parsedSend = message.metadata.template.components;
        } else if (message.sendStructure) {
          parsedSend = JSON.parse(message.sendStructure);
        }

        if (parsedSend && parsedTemplate) {
          const parameters = extractParametersFromSendStructure(parsedSend);
          const appliedTemplates = applyTemplateVariables(parameters, parsedTemplate);

          messageText = appliedTemplates?.components?.[0]?.text || t('SENT_A_TEMPLATE');
        }
      }
    } catch (error) {
      console.error('Error parsing template structures:', error);
      messageText = t('SENT_A_TEMPLATE');
    }
  } else if (message.channelType === 'EMAIL') {
    messageText = message.metadata?.subject || message.subject || stripHtml(messageContent);
    // Ensure even if we have subject, if messageContent exists, we use cleaned body as a fallback
    if (!message.metadata?.subject && !message.subject && messageContent) {
      messageText = stripHtml(messageContent);
    }
  } else if (
    message.contentType === 'button' ||
    message.contentType === 'interactive' ||
    message.contentType === 'INBOUND' ||
    message.contentType === 'OUTBOUND'
  ) {
    messageText = messageContent || getInteractiveMessageText(message) || (message.messageType === 'INBOUND' ? 'Interactive reply' : 'Interactive message');
  } else if (message.contentType === 'PRIVATE') {
    messageText = messageContent; // ✅ Use universal content
  } else {
    messageText =
      message.contentType !== 'text'
        ? message.messageType === 'INBOUND'
          ? t('RECEIVE_A_MEDIA')
          : t('SENT_A_MEDIA')
        : messageContent; // ✅ Use universal content
  }

  const displayText = `${sender}${messageText}`;

  // ✅ Show unread badge for INBOUND messages that haven't been read
  const status = message.messageType === 'INBOUND' && message.status?.toLowerCase() !== 'read';

  return { displayText, status };
};

const markdownToHtml = (text: string) => {
  if (!text) return '';
  return text
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/_(.*?)_/g, '$1')
    .replace(/~(.*?)~/g, '$1');
};

export function getNavItem({ conversation, currentUserId, t }: Props) {
  const { channel, contact, contactId } = conversation;

  const participantsInConversation = {
    ...contact,
    iconType: userType.find((user) => user.type === channel?.channelType),
  };

  const group = participantsInConversation.length > 1;
  const displayName = getDisplayName(channel, contact, conversation, t);

  // Process messages in priority order
  const messages = [
    { msg: conversation.lastMicrosoftTeamsMessage, prefix: 'msteam' },
    { msg: conversation.lastMessengerMessage, prefix: 'messenger' },
    { msg: conversation.lastInstagramMessage, prefix: 'instagram' },
    { msg: conversation.lastLineMessage, prefix: 'line' },
    { msg: conversation.lastTelegramMessage, prefix: 'telegram' },
    { msg: conversation.lastLiveChatMessage },
    { msg: conversation.lastEmail },
    { msg: conversation.lastMessage },
  ];

  let displayText = '';
  let status = false;

  for (const { msg, prefix } of messages) {
    if (msg) {
      const result = processMessage(msg, contact, contactId, t, prefix);
      displayText = result.displayText;
      status = result.status;
      break;
    }
  }

  const selectedProfileType = profileType.find((profile) => profile.type === channel.channelType);

  return {
    group,
    displayName,
    displayText: markdownToHtml(displayText),
    participants: participantsInConversation,
    lastActivity: conversation?.updatedAt,
    hasOnlineInGroup: group,
    selectedProfileType: { ...selectedProfileType, name: channel.channelName },
    messageStatus: status,
  };
}

export function getConversationName({ conversation, t }: Props) {
  return getDisplayName(conversation?.channel, conversation?.contact, conversation, t) || 'Unknown';
}
export function getConversationUserProfile({ conversation }: Props) {
  const userProfile =
    conversation?.contact?.ProfileURL ||
    conversation?.contact?.firstName ||
    conversation?.contact?.lastName;

  return userProfile || '';
}
export function getUserCallDetails({ conversation }: Props) {
  const userPhoneNumber = conversation?.contact?.phone || '';
  const userChannelType = conversation?.channel?.channelType || '';
  return { userPhoneNumber, userChannelType };
}
